import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Download,
  Search,
  Sparkles,
  ArrowRight,
  Calendar,
  Tag,
  BookOpen,
} from "lucide-react";

import PageHero from "@/components/shared/PageHero";
import Section from "@/components/shared/Section";
import Reveal from "@/components/shared/Reveal";
import GlowCard from "@/components/shared/GlowCard";
import Eyebrow from "@/components/shared/Eyebrow";

/* ----- Resources data (static for now — drop-in CMS-ready) ----- */
const ALL_RESOURCES = [
  {
    title: "AI Risk Whitepaper",
    type: "Whitepaper",
    date: "2024-10-01",
    desc: "Practical steps to identify and mitigate AI risks across the organization.",
    img: "https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg?auto=compress&cs=tinysrgb&w=800",
    link: "/assets/ai-risk-whitepaper.pdf",
    featured: true,
  },
  {
    title: "Automation Guide",
    type: "Guide",
    date: "2023-08-12",
    desc: "A tactical handbook for intelligent process automation and RPA augmentation.",
    img: "https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=800",
    link: "/assets/automation-guide.pdf",
  },
  {
    title: "AI Compliance eBook",
    type: "eBook",
    date: "2024-02-15",
    desc: "Frameworks and checklists to keep AI initiatives compliant and auditable.",
    img: "https://images.pexels.com/photos/1181244/pexels-photo-1181244.jpeg?auto=compress&cs=tinysrgb&w=800",
    link: "/assets/ai-compliance-ebook.pdf",
  },
  {
    title: "Case Study: Audit Automation",
    type: "Case Study",
    date: "2023-03-20",
    desc: "How we helped a financial client reduce manual audit effort by 60%.",
    img: "https://images.pexels.com/photos/3183171/pexels-photo-3183171.jpeg?auto=compress&cs=tinysrgb&w=800",
    link: "/assets/case-audit.pdf",
  },
  {
    title: "AI Ops Playbook",
    type: "Guide",
    date: "2024-07-10",
    desc: "A practical guide to operationalizing AI models with monitoring, retraining, and governance.",
    img: "https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=800",
    link: "/assets/ai-ops-playbook.pdf",
  },
  {
    title: "Case Study: Fraud Detection AI",
    type: "Case Study",
    date: "2024-05-22",
    desc: "How we reduced fraud losses by 80% with real-time risk scoring and explainable alerts.",
    img: "https://images.pexels.com/photos/7567443/pexels-photo-7567443.jpeg?auto=compress&cs=tinysrgb&w=800",
    link: "/assets/case-fraud-detection.pdf",
  },
];

const FILTERS = ["All", "Whitepaper", "Guide", "eBook", "Case Study"];

const Resources = () => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [visibleCount, setVisibleCount] = useState(6);

  const filtered = useMemo(() => {
    return ALL_RESOURCES.filter((r) => {
      const matchesFilter = filter === "All" || r.type === filter;
      const matchesQuery =
        !query ||
        r.title.toLowerCase().includes(query.toLowerCase()) ||
        r.desc.toLowerCase().includes(query.toLowerCase());
      return matchesFilter && matchesQuery;
    });
  }, [filter, query]);

  return (
    <div className="min-h-screen text-[var(--text-primary)]">
      <PageHero
        eyebrow="Resources"
        eyebrowIcon={BookOpen}
        title={
          <>
            Insights, guides &{" "}
            <span className="gradient-text">playbooks</span>
          </>
        }
        description="Curated knowledge to help you adopt AI responsibly and ship with confidence."
      >
        <Reveal variant="up" delay={0.18} duration={0.7}>
          <div className="mt-8 max-w-2xl mx-auto">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search resources, topics, or keywords"
                className="bg-[color:var(--input-bg)] border-[color:var(--border-default)] focus:border-[color:var(--brand-primary)] h-12 pl-11 pr-4 rounded-full text-sm"
              />
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                    filter === f
                      ? "btn-primary"
                      : "border border-[color:var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[color:var(--border-brand)]"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      </PageHero>

      {/* Grid */}
      <Section>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.slice(0, visibleCount).map((r, i) => (
            <Reveal key={r.title} variant="up" delay={(i % 3) * 0.06}>
              <article>
                <GlowCard className="p-0 overflow-hidden flex flex-col h-full">
                  <div className="h-44 w-full overflow-hidden relative">
                    <img
                      src={r.img}
                      alt={r.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                    {r.featured && (
                      <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-gradient-to-r from-[#9333EA] to-[#22D3EE] text-white">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="p-6 flex flex-col gap-3 flex-1">
                    <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
                      <span className="inline-flex items-center gap-1.5 font-medium">
                        <Tag className="w-3.5 h-3.5" /> {r.type}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" /> {new Date(r.date).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="font-display font-semibold text-lg leading-tight">{r.title}</h3>
                    <p className="text-[var(--text-secondary)] text-sm flex-1 leading-relaxed">{r.desc}</p>

                    <div className="mt-3 flex items-center justify-between gap-3">
                      <Link
                        to={r.link}
                        className="text-sm font-medium text-[var(--brand-primary-light)] hover:underline"
                      >
                        Preview
                      </Link>
                      <a
                        href={r.link}
                        download
                        className="inline-flex items-center gap-1.5 btn-primary rounded-full px-4 h-9 text-xs font-medium"
                      >
                        <Download className="w-3.5 h-3.5" /> Download
                      </a>
                    </div>
                  </div>
                </GlowCard>
              </article>
            </Reveal>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center mt-12">
            <p className="text-[var(--text-secondary)]">
              No resources found matching <strong>"{query}"</strong>
              {filter !== "All" && <> in <strong>{filter}</strong></>}.
            </p>
          </div>
        )}

        {visibleCount < filtered.length && (
          <div className="mt-10 text-center">
            <Button
              onClick={() => setVisibleCount((c) => c + 6)}
              variant="outline"
              className="btn-outline rounded-full px-6 h-11"
            >
              Load more
            </Button>
          </div>
        )}
      </Section>

      {/* Featured + subscribe */}
      <Section>
        <div className="grid md:grid-cols-2 gap-5 max-w-6xl mx-auto">
          <Reveal variant="left">
            <GlowCard variant="liquid" className="aurora p-7 h-full overflow-hidden">
              <div className="relative z-10">
                <Eyebrow icon={Sparkles} className="mb-3">Featured</Eyebrow>
                <h4 className="font-display font-bold text-xl mb-3">
                  AI Risk <span className="gradient-text">Whitepaper</span>
                </h4>
                <p className="text-[var(--text-secondary)] text-sm mb-5">
                  Practical steps to evaluate AI risk in your organization.
                </p>
                <a
                  href="/assets/ai-risk-whitepaper.pdf"
                  download
                  className="btn-primary rounded-full px-5 h-11 inline-flex items-center gap-2 text-sm"
                >
                  <Download className="w-4 h-4" /> Download
                </a>
              </div>
            </GlowCard>
          </Reveal>

          <Reveal variant="right">
            <GlowCard className="p-7 h-full">
              <Eyebrow icon={ArrowRight} className="mb-3">Subscribe</Eyebrow>
              <h4 className="font-display font-bold text-xl mb-3">
                Get resources in your <span className="gradient-text">inbox.</span>
              </h4>
              <p className="text-[var(--text-secondary)] text-sm mb-5">
                Subscribe for new guides, case studies and event updates.
              </p>
              <form className="flex flex-col sm:flex-row gap-2 max-w-md" onSubmit={(e) => e.preventDefault()}>
                <Input
                  placeholder="you@company.com"
                  className="bg-[color:var(--input-bg)] border-[color:var(--border-default)] focus:border-[color:var(--brand-primary)] h-11 rounded-full px-4 flex-1"
                />
                <Button className="btn-primary rounded-full px-6 h-11">
                  Subscribe
                </Button>
              </form>
            </GlowCard>
          </Reveal>
        </div>
      </Section>
    </div>
  );
};

export default Resources;
